/**
 * Game-start mode setup: place every active layer in the generated world.
 *
 * Lifted out of Game.startGame so it composes — one objective layer plus
 * any environment layers plus respawn — and so headless matches set up the
 * same way the game does. Runs after map generation, before deploy.
 *
 * Pure function — no side effects.
 */

import { placeCapturePoints, placeSectors, startPush } from './capturePoints';
import { placeOpenObjectives } from './openLayout';
import { ruinJob } from './body';
import type { GameState, RuinState } from '../types';
import { SeededRandom } from './random';
import { generateCarryables } from './mapgen';
import { vecCross, vecLength, vecNormalize, vecSlerp } from '../math';
import { getModeRules, defaultRespawnTickets } from './modeRules';
import { initModeState } from './modes';
import { assignHomeTeamPlanets } from './deploy';
import { initSignalTrace, makeRuinsInteractable } from './signalTrace';
import { initUplink } from './uplink';
import { initSalvageRun } from './salvageRun';
import { initExcavation } from './excavation';
import { initRespawnState } from './respawn';
import { computeFlagStatus } from './carryables';
import { dcos, dsin } from './dmath';

/**
 * Guarantee at least one antenna ruin (signal trace posts and uplink uploads
 * need one). Deterministic from the seed.
 */
function ensureAntennaRuin(state: GameState): GameState {
  if (state.ruins.some(r => ruinJob(r).uplink)) return state;
  const rng = new SeededRandom(state.config.seed + '-antenna-fix');
  const spawnable = state.planets.filter(p => p.type === 'planet' && p.alive);
  if (spawnable.length === 0) return state;
  const planet = spawnable[rng.int(0, spawnable.length - 1)];
  const theta = rng.range(0, Math.PI * 2);
  const phi = rng.range(0.3, Math.PI - 0.3);
  const nextRuinId = state.ruins.length > 0 ? Math.max(...state.ruins.map(r => r.id)) + 1 : 0;
  const antenna = {
    id: nextRuinId,
    ruinType: 'antenna',
    planetIndex: state.planets.indexOf(planet),
    surfaceDir: {
      x: dsin(phi) * dcos(theta),
      y: dcos(phi),
      z: dsin(phi) * dsin(theta),
    },
    alive: true,
    interactable: false,
    interactTurns: undefined,
    interactEffect: undefined,
  } as RuinState;
  return { ...state, ruins: [...state.ruins, antenna] };
}

/**
 * Build the match's mode state from its config and generated world:
 * objective placements, environment schedules, respawn ticket pools.
 */
export function setupModeLayers(input: GameState): GameState {
  const config = input.config;
  const seed = config.seed;
  const rules = getModeRules(config.modeConfig);
  let state: GameState = { ...input, modeState: initModeState(config.modeConfig, input.planets, seed) };

  switch (rules.objective) {
    case 'ctf': {
      const rng = new SeededRandom(seed + '-ctf');
      const homeTeamPlanets = assignHomeTeamPlanets(state.planets, config.numTeams, rng);
      state = {
        ...state,
        config: { ...state.config, modeConfig: { ...state.config.modeConfig, homeTeamPlanets } },
        // Cores spawn next to the drop cans once deploy completes
        carryables: [],
        modeState: { ...state.modeState, ctf: { scores: {}, flagStatus: computeFlagStatus([]) } },
      };
      break;
    }
    case 'signalTrace': {
      state = ensureAntennaRuin(state);
      const rng = new SeededRandom(seed + '-signaltrace');
      const signal = initSignalTrace(state.planets, rng);
      state = {
        ...state,
        ruins: makeRuinsInteractable(state.ruins, config.modeConfig),
        modeState: {
          ...state.modeState,
          signalTrace: {
            signalPlanetIndex: signal.signalPlanetIndex,
            signalSurfaceDir: signal.signalSurfaceDir,
            clueState: { activatedPosts: {}, clues: {}, signalRevealed: {} },
            extraction: null,
            extractionCompletedTeam: null,
          },
        },
      };
      break;
    }
    case 'uplink': {
      state = ensureAntennaRuin(state);
      const rng = new SeededRandom(seed + '-uplink');
      const uplink = initUplink(state.planets, state.ruins, rng, config.modeConfig);
      state = {
        ...state,
        collectibles: uplink.collectibles,
        modeState: { ...state.modeState, uplink: { antennaRuinId: uplink.antennaRuinId, uploadedFragments: {} } },
      };
      break;
    }
    case 'salvage': {
      const rng = new SeededRandom(seed + '-salvage');
      const salvage = initSalvageRun(state.planets, state.players, rng, config.modeConfig);
      state = {
        ...state,
        collectibles: salvage.collectibles,
        modeState: { ...state.modeState, salvage: { depotPlanets: salvage.depotPlanets, deliveredValue: {} } },
      };
      break;
    }
    case 'excavation': {
      const rng = new SeededRandom(seed + '-excavation');
      const excav = initExcavation(state.planets, rng);
      state = {
        ...state,
        modeState: {
          ...state.modeState,
          excavation: {
            artifactPlanetIndex: excav.artifactPlanetIndex,
            artifactSurfaceDir: excav.artifactSurfaceDir,
            drillSites: [],
            artifactExtracted: false,
            artifactCarryableId: null,
          },
        },
      };
      break;
    }
    case 'stakes':
    case 'none':
      break;
  }

  if (rules.respawn) {
    const tickets = config.modeConfig.respawnTickets ?? defaultRespawnTickets(rules.objective);
    state = { ...state, modeState: { ...state.modeState, respawn: initRespawnState(state.players, tickets) } };
  }

  return state;
}

/** First unit's planet per team, after deploy. */
function deployedPlanetByTeam(state: GameState): Record<number, number> {
  const byTeam: Record<number, number> = {};
  for (const p of state.players) {
    if (byTeam[p.teamIndex] === undefined) byTeam[p.teamIndex] = p.planetIndex;
  }
  return byTeam;
}

/**
 * After deploy: layers whose placements follow where the teams actually
 * landed. CTF home planets and cores move to the deployed planets (cores beside
 * the team's crashed drop can when there is one); salvage depots move to the
 * deployed planets. Pure; the game rebuilds meshes from the result.
 */
export function finalizeModeDeploy(input: GameState): GameState {
  let state = input;

  // 59.6: capture points go between where the teams actually landed
  const pts = state.modeState.points;
  if (pts && pts.sites.length === 0) {
    // 69.6b: the 'sectors' layout also fixes the front its sectors run along
    const layout = state.config.modeConfig.pointLayout;
    const placed = layout === 'sectors' ? placeSectors(state) : { sites: layout === 'open' ? placeOpenObjectives(state) : placeCapturePoints(state) };   // (§0.11 step A: 'open')
    state = { ...state, modeState: { ...state.modeState, points: { ...pts, sites: placed.sites, ...(placed.front ? { front: placed.front } : {}) } } };
    state = startPush(state);   // 69.6d: an Operations push, when asked for
  }

  const ctf = state.modeState.ctf;
  if (ctf) {
    const homeTeamPlanets = deployedPlanetByTeam(state);
    state = {
      ...state,
      config: { ...state.config, modeConfig: { ...state.config.modeConfig, homeTeamPlanets } },
    };
    const rng = new SeededRandom(state.config.seed + '-ctf-deploy');
    const carryables = generateCarryables(state.planets, state.config.modeConfig, rng);
    for (const core of carryables) {
      const can = state.crashedDropCans.find(c => c.teamIndex === core.teamIndex && c.planetIndex === core.planetIndex);
      if (!can) continue;
      // Offset the core ~0.15 radians from the drop can so it sits beside it
      const canDir = vecNormalize(can.surfaceDir);
      let tangent = vecCross(canDir, { x: 0, y: 1, z: 0 });
      if (vecLength(tangent) < 0.01) tangent = vecCross(canDir, { x: 1, y: 0, z: 0 });
      tangent = vecNormalize(tangent);
      const offsetDir = vecNormalize(vecSlerp(canDir, tangent, 0.15));
      core.surfaceDir = { ...offsetDir };
      core.originSurfaceDir = { ...offsetDir };
    }
    state = {
      ...state,
      carryables,
      modeState: { ...state.modeState, ctf: { ...ctf, flagStatus: computeFlagStatus(carryables) } },
    };
  }

  if (state.modeState.salvage) {
    state = {
      ...state,
      modeState: { ...state.modeState, salvage: { ...state.modeState.salvage, depotPlanets: deployedPlanetByTeam(state) } },
    };
  }

  return state;
}
